import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Avatar,
  IconButton,
  Divider,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Emergency as EmergencyIcon,
  LockReset as LockResetIcon,
  LocationOn as LocationIcon,
  FitnessCenter as FitnessCenterIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { userService } from '../services/userService';
import { userProfileService } from '../services/userProfileService';
import { UserProfile as UserProfileType, UpdateUserProfileRequest, UserRole, PreferredTrainingInfo } from '../types/user';
import { useAuth } from '../contexts/AuthContext';
import { DOB_MIN, dateOfBirthError, dobMax } from '../utils/dateOfBirth';
import { ageOn } from '../utils/profileUtils';
import UserNotes from './UserNotes';
import { API_BASE_URL } from '../utils/config';
import { sosAccessService } from '../services/sosAccessService';
// [3. javító kör, F-1, 2026-10-03] lejárat-tudatos burkolók (`utils/saveErrors.ts`)
import { requestErrorMessage, saveErrorMessage } from '../utils/saveErrors';
// [Web UX 2. csomag – Belépés és munkamenet, 2026-10-03] A `fetch`-ágak 401-ét semmilyen axios-interceptor
// nem látja (leltár 2(a)): minden hitelesített hívás után a közös jelzőnek adjuk át (`utils/session.ts`).
// [Web 7 / T1, 2026-10-05] a hitelesített `fetch`-ek a közös `authFetch` burkolón mennek: token, csendes
// megújítás, 401 utáni egyszeri ismétlés és a lejárat-jelzés (`reportFetchUnauthorized`) EGY helyen.
import { authFetch } from '../utils/authFetch';
import { useFeedback } from '../contexts/FeedbackContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import { isFormDirty, discardChangesConfirm } from '../utils/formDirty';
import { isForintInputAccepted, parseForintInput, forintFieldValue, isUnfinishedForintDraft, unfinishedForintMessage } from '../utils/money';
// A szerepkor-dontesek EGY helyen, a GEPI nevbol [B-W4, 2026-09-16] — lasd `utils/roles.ts`.
import { isCoachRole, isPlayerRole } from '../utils/roles';

interface UserProfileDialogProps {
  open: boolean;
  onClose: () => void;
  userId?: string; // If provided, shows this user's profile, otherwise shows current user's profile
}

const DAY_ORDER: Record<string, number> = {
  'Monday': 0, 'Tuesday': 1, 'Wednesday': 2, 'Thursday': 3,
  'Friday': 4, 'Saturday': 5, 'Sunday': 6,
};

const DAY_NAMES_HU: Record<string, string> = {
  'Monday': 'Hétfő', 'Tuesday': 'Kedd', 'Wednesday': 'Szerda', 'Thursday': 'Csütörtök',
  'Friday': 'Péntek', 'Saturday': 'Szombat', 'Sunday': 'Vasárnap',
};

const UserProfileDialog: React.FC<UserProfileDialogProps> = ({ open, onClose, userId }) => {
  const { user } = useAuth();
  const { showSuccess, showError } = useFeedback();
  const confirm = useConfirm();
  const isAdmin = user?.userRole === 'Administrator';
  const isCoach = user?.userRole === 'Coach' || user?.userRole === 'Edző';

  // Check if viewing another user's profile (works for both Admin and Coach)
  const isViewingOtherUser = userId && userId !== user?.userId;

  // Only Administrator can edit user profiles (even coaches cannot edit)
  const canEdit = isAdmin;

  // SOS feature: Coach viewing another user needs SOS to see sensitive data
  const needsSosAccess = isCoach && isViewingOtherUser;

  // [Web 6 W1, 2026-10-05] AZ ÓRADÍJ CSAK ADMINNAK VAGY A SAJÁT ADATLAPON. A klubvezetés kérése:
  // „Az edző óradíja a többi edző számára továbbra is látható — egymás óradíját nem kéne, hogy lássák."
  // A szerver más adatlapján nem-adminnak már `null`-t ad (`backend/PinyoApi/Services/UserProfileSanitizer.cs`);
  // ha a mező ettől még látszana, edzőnél egy ÜRES „Óradíj (Ft)" mező félrevezetően azt sugallná, hogy a
  // kollégának nincs óradíja. Ezért a mező más adatlapján edzőnek (SOS után sem) nem jelenik meg.
  // Mentési kockázat nincs: nem-admin itt nem menthet (`canEdit = isAdmin`).
  const canSeeHourlyRate = isAdmin || !isViewingOtherUser;

  const [profile, setProfile] = useState<UserProfileType | null>(null);
  const [groupTrainings, setGroupTrainings] = useState<PreferredTrainingInfo[]>([]);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [formData, setFormData] = useState<UpdateUserProfileRequest>({
    name: '',
    email: '',
    phone: '',
    venueId: undefined,
    preferredTrainingIds: [],
    bankAccount: '',
    billingName: '',
    billingZipCode: '',
    billingCity: '',
    billingStreetAddress: '',
    taxNumber: '',
    hourlyRate: undefined,
    privateLessonFee: undefined,
    dateOfBirth: '',
    parentName: '',
    parentPhone: '',
    address: '',
    userRoleId: undefined,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [uploadingImage, setUploadingImage] = useState(false);
  /**
   * A jelszó-reset eredménye: SZÁNDÉKOSAN TARTÓS, kézzel zárható Alert (nem toast) — az adminnak
   * el kell olvasnia, hogy MELY címre ment a levél, és ezt egy 4 mp után eltűnő csíkban
   * könnyű elszalasztani. Minden MÁS sikerüzenet ebben a dialógusban a globális `showSuccess`
   * toastra megy.
   *
   * ⚠️ JELSZÓ NEM KERÜL IDE [É1, 2026-09-13]: a felület korábban a `Jatekos2026!` LITERÁLT írta
   * ki („Az új jelszó ez lesz…"), miközben a szerver (`UsersController.ResetPassword`) VÉLETLEN
   * ideiglenes jelszót generál, azt csak e-mailben küldi el, és a válaszban nem adja vissza →
   * az admin egy NEM MŰKÖDŐ jelszót adott át a klubtagnak. A válaszból csak az e-mail cím
   * használható fel, azt írjuk ki.
   */
  const [passwordResetMessage, setPasswordResetMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  /** A két pénz-mező DOM-eleme — a mentés-őr ide fókuszál és ide görget [M2, 2026-09-15]. */
  const forintInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  /**
   * A BETÖLTÖTT adatok, mint alapvonal — ehhez hasonlítjuk az űrlapot a mentetlen-változás
   * védelemhez. `null` = még nem töltött be (nyitás, betöltés közben), ilyenkor soha nem piszkos.
   * A `handleSave` végén futó `loadProfile()` újra beállítja, tehát mentés után nincs kérdés.
   */
  const [baseline, setBaseline] = useState<UpdateUserProfileRequest | null>(null);
  /**
   * A PÉNZ-MEZŐKBE GÉPELT nyers szöveg (mezőnév → szöveg) — CSAK MEGJELENÍTÉSRE [BL-2b, 2026-09-15].
   *
   * MIÉRT KELL: a két pénz-mező állapota `number | undefined`, tehát a félbe-takarított „2."
   * (egy RÉGI, tizedes érték javításának első leütése) a `parseForintInput`-on `undefined` lett, és
   * a mező a felhasználó szeme előtt KIÜRÜLT — miközben a szerveren még a 2,8 állt. Ha ilyenkor
   * mentett, a PUT-ból a mező KIMARADT, és a szerver a TÁROLT óradíjat/díjat NULL-ra írta
   * (`backend/PinyoApi/Controllers/UserProfileController.cs:404` `user.HourlyRate = dto.HourlyRate`,
   * feltétel nélkül) — utána minden kifizetés-számítás díj nélkül futott.
   *
   * ⚠️ A MENTENDŐ ÉRTÉK TOVÁBBRA IS a `formData` SZÁMA: ez a vázlat kizárólag a mező
   * MEGJELENÍTÉSÉT és a mentés-őrt szolgálja, tehát a mentetlen-változás ALAPVONALÁNAK alakja
   * (`baseline` ↔ `formData`) NEM változott — pont ezt a kockázatot nevezte meg a `money.ts` NB-1
   * döntése, amikor a vázlatot elutasította.
   */
  const [forintDrafts, setForintDrafts] = useState<{ hourlyRate?: string; privateLessonFee?: string }>({});
  /** A mezőben MEGJELENÍTETT szöveg: a gépelt nyers alak, ha van, különben a mentendő szám. */
  const forintFieldText = (name: 'hourlyRate' | 'privateLessonFee') =>
    forintDrafts[name] ?? forintFieldValue(formData[name]);

  // SOS Access State
  const [sosGranted, setSosGranted] = useState(false);
  const [sosEligible, setSosEligible] = useState(false);
  const [sosCheckingEligibility, setSosCheckingEligibility] = useState(false);
  const [sosGranting, setSosGranting] = useState(false);
  const [sosMessage, setSosMessage] = useState<string>('');

  // Password Reset State
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  // SOS Access Functions
  const checkSosEligibility = async (viewedUserId: string) => {
    try {
      setSosCheckingEligibility(true);
      const eligibility = await sosAccessService.checkEligibility(viewedUserId);
      setSosEligible(eligibility.isEligible);
      setSosMessage(eligibility.reason);
    } catch (err: any) {
      console.error('Error checking SOS eligibility:', err);
      setSosEligible(false);
      setSosMessage('Hiba történt a jogosultság ellenőrzésekor');
    } finally {
      setSosCheckingEligibility(false);
    }
  };

  const handleSosButtonClick = async () => {
    if (!userId) return;

    try {
      setSosGranting(true);
      setError('');
      const response = await sosAccessService.grantAccess(userId);

      if (response.isGranted) {
        setSosGranted(true);
        // ══════════════════════════════════════════════════════════════════════════
        // ÚJRA BE KELL TÖLTENI A PROFILT AZ SOS UTÁN [BL-A, 2026-09-16]
        // ══════════════════════════════════════════════════════════════════════════
        // MI VÁLTOZOTT: az SOS eddig CSAK egy KLIENS-oldali jelző volt — a szerver a
        // személyes mezőket (szülői elérhetőség, cím, születési dátum, számlázási adatok)
        // MÁR a dialógus megnyitásakor kiadta, és a felület csak ELREJTETTE. Ez azt
        // jelentette, hogy egy `curl` vagy a böngésző fejlesztőeszköze megkerülte az
        // SOS-kaput. A kapu ezért átkerült a SZERVERRE
        // (`backend/PinyoApi/Services/UserProfileSanitizer.cs`): SOS nélkül a szerver
        // `null`-t ad ezekre a mezőkre.
        // EBBŐL KÖVETKEZIK EZ A SOR: a megnyitáskor betöltött profil ezeket a mezőket
        // ÜRESEN tartalmazza, tehát az SOS-gomb után ÚJRA le kell kérni — enélkül a gomb
        // megnyomása után a szakaszok megjelennének, de ÜRESEN, és az edző azt hinné,
        // hogy a klub nem adott meg szülői telefonszámot. (Mérve: `UserProfileDialog.sos.test.tsx`.)
        // ⚠️ MENTETLEN MÓDOSÍTÁST NEM ÍRHAT FELÜL: ez az ág CSAK edzőnél fut
        // (`needsSosAccess = isCoach && isViewingOtherUser`), az edző pedig nem
        // szerkeszthet (`canEdit = isAdmin`), tehát nincs mit elveszíteni.
        await loadProfile();
        showSuccess('SOS hozzáférés engedélyezve. Az érzékeny adatok most láthatók.');
      } else {
        // ⚠️ EZ AZ ÁG MA ELÉRHETETLEN [K-3, 2026-09-16] — de SZÁNDÉKOSAN itt marad.
        // MIÉRT elérhetetlen: az elutasítást a szerver 403-cal adja
        // (`backend/PinyoApi/Controllers/SosAccessController.cs`, a `GrantAccess` végpont), nem
        // 200-as `isGranted:false`-szal. ⚠️ 2026-09-16 óta a SZERVER-HIBA ugyanott 500-at ad
        // (`isServerError` — a hiba ne „nincs jogosultságod"-ként jelenjen meg); a kliens
        // szempontjából mindkettő ugyanaz az út: az axios KIVÉTELT dob, és a lenti `catch` fut, ahol a
        // szerver MAGYAR indoklása `requestErrorMessage`-dzsel (belül `apiErrorMessage`) jut ki. (A `UserProfileDialog.sos.test.tsx`
        // ezért a VALÓDI 403-as utat méri; korábban a teszt egy 200-as `isGranted:false`
        // választ hamisított, amit a szerver soha nem ad — az a teszt tehát egy NEM LÉTEZŐ
        // viselkedést igazolt.)
        // MIÉRT MARAD: ha egy jövőbeli szerver-változás mégis 200-at adna `isGranted:false`-szal,
        // az ág nélkül a gombnyomás NÉMÁN, visszajelzés nélkül elhalna — ami rosszabb, mint egy
        // sosem futó `setError`.
        setError(response.message);
      }
    } catch (err: any) {
      console.error('Error granting SOS access:', err);
      setError(requestErrorMessage(err, 'Hiba történt az SOS hozzáférés engedélyezésekor'));
    } finally {
      setSosGranting(false);
    }
  };

  const handleResetPasswordClick = async () => {
    const confirmed = await confirm({
      title: 'Jelszó alaphelyzetbe állítása',
      message: (
        <>
          Biztosan alaphelyzetbe szeretnéd állítani a felhasználó jelszavát?
          <Typography variant="body2" sx={{ mt: 2 }}>
            A rendszer új, véletlen ideiglenes jelszót állít be, és <strong>e-mailben elküldi a
            felhasználónak</strong>. A jelszó itt nem jelenik meg, és a régi jelszó azonnal
            érvénytelen lesz.
          </Typography>
        </>
      ),
      confirmLabel: 'Igen, állítsd alaphelyzetbe',
      destructive: true,
    });
    if (!confirmed) return;

    if (!userId) return;

    try {
      setIsResettingPassword(true);
      const result = await userService.resetPassword(userId);
      // TARTÓS Alert, nem toast — lásd a `passwordResetMessage` deklarációjánál a kommentet.
      // Jelszó SOHA nem kerül ide: a szerver nem ad vissza jelszót, csak az e-mail címet.
      const targetEmail = result?.userEmail || formData.email || profile?.email;
      setPasswordResetMessage(
        targetEmail
          ? `A jelszó alaphelyzetbe állítva. Az új, ideiglenes jelszót e-mailben elküldtük a felhasználónak: ${targetEmail}`
          : 'A jelszó alaphelyzetbe állítva. Az új, ideiglenes jelszót e-mailben elküldtük a felhasználónak.'
      );
    } catch (err: any) {
      console.error('Password reset failed:', err);
      setError(requestErrorMessage(err, 'Hiba történt a jelszó alaphelyzetbe állításakor'));
    } finally {
      setIsResettingPassword(false);
    }
  };

  useEffect(() => {
    if (open) {
      // Reset SOS state on open
      setSosGranted(false);
      setSosEligible(false);
      setSosMessage('');
      // A jelszó-visszaállítás tartós üzenete SOHA ne szivárogjon át egy másik felhasználó
      // adatlapjára: a záráskor is töröljük, de ha a szülő programból nyitja újra, itt is.
      setPasswordResetMessage(null);
      // Nyitáskor nincs alapvonal: amíg a profil be nem töltött, az űrlap (esetleg egy MÁSIK
      // felhasználó előző adataival) nem számít mentetlen módosításnak.
      setBaseline(null);

      loadProfile();
      loadGroupTrainings();
      if (isAdmin && isViewingOtherUser) {
        loadRoles();
      }

      // Check SOS eligibility if coach is viewing another user
      if (needsSosAccess && userId) {
        checkSosEligibility(userId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId]);

  const loadGroupTrainings = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/planned-events/group-trainings`);
      if (!response.ok) throw new Error('Failed to load group trainings');
      const data = await response.json();
      if (Array.isArray(data)) {
        setGroupTrainings(data);
      } else {
        setGroupTrainings([]);
      }
    } catch (err) {
      console.error('Error loading group trainings:', err);
      setGroupTrainings([]);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await authFetch(`${API_BASE_URL}/api/user-roles`);

      if (!response.ok) return;

      const result = await response.json();
      const rolesArray = result.data || result;
      if (Array.isArray(rolesArray)) {
        const mappedRoles = rolesArray.map((r: any) => ({
          userRoleId: r.UserRoleId || r.userRoleId,
          name: r.Name || r.name,
          displayName: r.DisplayName || r.displayName,
          displayOrder: r.DisplayOrder || r.displayOrder || 0,
          isActive: r.IsActive ?? r.isActive ?? true,
        }));
        setRoles(mappedRoles);
      }
    } catch (err) {
      console.error('Error loading roles:', err);
    }
  };

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError('');
      /**
       * A PÉNZ-VÁZLATOK NULLÁZÁSA A BETÖLTÉS ELŐTT IS [S2, 2026-09-15].
       * MI VOLT A BAJ: a nullázás CSAK az `await` UTÁN, a sikeres ágon futott — egy ELBUKOTT
       * betöltés után a KORÁBBI felhasználó félbehagyott, gépelt óradíja/magánóra-díja ott
       * maradt a mezőben, MÁS profil adatai fölött („szellem-vázlat"), és a mentés azt küldte
       * volna ki. A `UserManagement` már a dialógus MEGNYITÁSAKOR nulláz — itt is ez a pont.
       */
      setForintDrafts({});
      const profileData = isViewingOtherUser && userId
        ? await userProfileService.getUserProfileById(userId)
        : await userProfileService.getMyProfile();
      setProfile(profileData);
      const loaded: UpdateUserProfileRequest = {
        name: profileData.name || '',
        email: profileData.email || '',
        phone: profileData.phone || '',
        venueId: profileData.venueId || undefined,
        preferredTrainingIds: profileData.preferredTrainingIds || [],
        bankAccount: profileData.bankAccount || '',
        billingName: profileData.billingName || '',
        billingZipCode: profileData.billingZipCode || '',
        billingCity: profileData.billingCity || '',
        billingStreetAddress: profileData.billingStreetAddress || '',
        taxNumber: profileData.taxNumber || '',
        // `?? undefined`, NEM `|| undefined` [N-12]: a tárolt 0 Ft érdemi érték, a `||` viszont
        // „nincs megadva"-ként hozta be — a szomszédos `privateLessonFee` már eddig is `??`-t
        // használt, egy fogalom nem lehet két szabály.
        hourlyRate: profileData.hourlyRate ?? undefined,
        privateLessonFee: profileData.privateLessonFee ?? undefined,
        dateOfBirth: profileData.dateOfBirth ? profileData.dateOfBirth.split('T')[0] : '',
        parentName: profileData.parentName || '',
        parentPhone: profileData.parentPhone || '',
        address: profileData.address || '',
        userRoleId: profileData.userRoleId || undefined,
      };
      setFormData(loaded);
      // A vázlatok a BETÖLTÖTT értékkel elavulnak — enélkül egy régi gépelt szöveg maradna a mezőben.
      setForintDrafts({});
      // Alapvonal a betöltés UTÁN, PONTOSAN az űrlapba tett objektummal.
      setBaseline(loaded);
    } catch (err: any) {
      console.error('Error loading profile:', err.response?.status ?? 'nincs válasz', err);
      setError(requestErrorMessage(err, 'Hiba történt a profil betöltésekor'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // PÉNZ-MEZŐK: csak SZÁMJEGY kerülhet be, EGÉSZ forint [BL-2/N-8, 2026-09-14]. A `parseFloat`
    // korábban a magyarul gépelt `2.800`-ból (kétezer-nyolcszáz) 2,8 Ft-ot csinált, NÉMÁN — az
    // indoklást és a mérést lásd a `utils/money.ts` fejlécében. A nem illeszkedő leütést EL SEM
    // tároljuk, tehát a mező a korábbi tartalmát mutatja tovább.
    // A MOSTANI tartalmat is átadjuk, hogy egy RÉGI, tizedes érték takarítható legyen [J-3].
    const isForintField = name === 'hourlyRate' || name === 'privateLessonFee';
    // A MOSTANI tartalom a MEGJELENÍTETT szöveg (a vázlat, ha van) — nem a szám-állapot [BL-2b]:
    // különben a „2.8" → „2." lépés után a következő leütést már egy ÜRES mostani értékhez
    // mértük volna, és a takarítás félúton elakadt volna.
    if (isForintField && !isForintInputAccepted(value, forintFieldText(name as 'hourlyRate' | 'privateLessonFee'))) return;
    if (isForintField) {
      // A gépelt nyers alak LÁTSZIK (a félbe-takarított „2." is), a mentendő szám külön áll.
      setForintDrafts(prev => ({ ...prev, [name]: value }));
    }

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    setFormData((prev) => ({
      ...prev,
      [name]: isForintField
        ? parseForintInput(value)
        : name === 'dateOfBirth'
          ? (value || '')
          : value,
    }));
  };

  const handleTrainingToggle = (plannedEventId: string) => {
    setFormData((prev) => {
      const current = prev.preferredTrainingIds || [];
      const updated = current.includes(plannedEventId)
        ? current.filter(id => id !== plannedEventId)
        : [...current, plannedEventId];
      return { ...prev, preferredTrainingIds: updated };
    });
  };

  // Calculate age for conditional parent field validation
  const getAge = (): number | null => {
    const dateOfBirth = formData.dateOfBirth || profile?.dateOfBirth;
    if (!dateOfBirth) return null;
    return ageOn(dateOfBirth, new Date());
  };

  // Egy konkrét mező validálása (onBlur eseményhez)
  const validateField = (fieldName: string, value: string | undefined): string | null => {
    const age = getAge();
    const isUnder18 = age !== null && age < 18;

    switch (fieldName) {
      case 'name':
        if (!value?.trim()) {
          return 'A név megadása kötelező';
        }
        return null;

      case 'email':
        if (!value?.trim()) {
          return 'Az e-mail cím megadása kötelező';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return 'Érvénytelen e-mail cím formátum';
        }
        return null;

      case 'phone':
        if (!value?.trim()) {
          return 'A telefonszám megadása kötelező';
        } else if (!/^\+36\d{9}$/.test(value.replace(/\s/g, ''))) {
          return 'Érvénytelen telefonszám formátum. Helyes formátum: +36201234567';
        }
        return null;

      case 'dateOfBirth':
        return dateOfBirthError(value ?? '') || null;

      case 'parentName':
        if (isUnder18 && !value?.trim()) {
          return '18 év alatti játékosok esetén kötelező a szülő nevének megadása';
        }
        return null;

      case 'parentPhone':
        if (isUnder18) {
          if (!value?.trim()) {
            return '18 év alatti játékosok esetén kötelező a szülő telefonszámának megadása';
          } else if (!/^\+36\d{9}$/.test(value.replace(/\s/g, ''))) {
            return 'Érvénytelen telefonszám formátum. Helyes formátum: +36201234567';
          }
        }
        return null;

      default:
        return null;
    }
  };

  const handleFieldBlur = (fieldName: string) => {
    const value = formData[fieldName as keyof typeof formData] as string;
    const error = validateField(fieldName, value);

    setFieldErrors((prev) => ({
      ...prev,
      [fieldName]: error || '',
    }));
  };

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    // Kötelező mezők validációja
    const nameError = validateField('name', formData.name);
    if (nameError) errors.name = nameError;

    const emailError = validateField('email', formData.email);
    if (emailError) errors.email = emailError;

    const phoneError = validateField('phone', formData.phone);
    if (phoneError) errors.phone = phoneError;

    const dateOfBirthError = validateField('dateOfBirth', formData.dateOfBirth);
    if (dateOfBirthError) errors.dateOfBirth = dateOfBirthError;

    // 18 év alattiak esetén szülő adatai kötelezőek
    const age = getAge();
    const isUnder18 = age !== null && age < 18;
    if (isUnder18) {
      const parentNameError = validateField('parentName', formData.parentName);
      if (parentNameError) errors.parentName = parentNameError;

      const parentPhoneError = validateField('parentPhone', formData.parentPhone);
      if (parentPhoneError) errors.parentPhone = parentPhoneError;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    setError('');

    /**
     * KÖZTES PÉNZ-VÁZLAT: a mentés MEGÁLL, a MEZŐ MEGNEVEZÉSÉVEL [BL-2b, 2026-09-15].
     *
     * MI VOLT A BAJ (mérve): egy RÉGI, tizedes óradíj takarításának első leütése (`"5500.5"` →
     * `"5500."`) elfogadott (`isForintInputAccepted`, J-3), de a `parseForintInput` erre
     * `undefined`-ot ad. A PUT-ból így a `hourlyRate` KIMARADT, a szerver pedig FELTÉTEL NÉLKÜL
     * ír (`UserProfileController.cs:404`) — az edző óradíja NULL lett, és onnantól minden
     * kifizetés-számítás díj nélkül futott. A mező közben üres volt, tehát a felhasználó nem
     * tudta, hogy a szerveren MÉG volt érték, amit a mentése törölni fog.
     *
     * MIÉRT BLOKKOLÁS, nem a helyszín-űrlap TARTALÉK-ÁGA: a vázlat csak felhasználói leütésre jön
     * létre, tehát a félbehagyott takarítás megkülönböztethető az érintetlen mezőtől — a tartalék
     * viszont NÉMÁN a régi számot mentené. Az indoklást lásd `utils/money.ts`.
     *
     * A SZÁNDÉKOS TÖRLÉS ÉRINTETLEN: az üres mező (`""`) számjegy-alakú, tehát nem köztes vázlat —
     * `undefined` megy ki, és a szerver null-t ment („nincs megadva").
     */
    const unfinishedField = ([
      ['hourlyRate', 'Óradíj (Ft)'],
      ['privateLessonFee', 'Magánóra-díj (Ft)'],
    ] as const).find(([name]) => isUnfinishedForintDraft(forintDrafts[name]));
    if (unfinishedField) {
      /**
       * ⚠️ A BLOKKOLÁS NEM LEHET NÉMA [M2, 2026-09-15]. A `setError` a dialógus TETEJÉN lévő
       * Alertbe megy (`:729`), a pénz-mezők viszont jóval lejjebb (`:1183`) — a felhasználó a
       * Mentés megnyomása után SEMMIT nem látott. Ezért a hiba HÁROM helyen jelenik meg: a
       * felső sáv, egy TOAST, és MAGA A MEZŐ (piros keret + súgó), amit oda is görgetünk.
       */
      const message = unfinishedForintMessage(unfinishedField[1]);
      setError(message);
      showError(message);
      setFieldErrors(prev => ({ ...prev, [unfinishedField[0]]: message }));
      const input = forintInputRefs.current[unfinishedField[0]];
      // jsdom-ban a `scrollIntoView` nem létezik — a `?.` NEM kozmetika.
      input?.scrollIntoView?.({ block: 'center' });
      input?.focus?.();
      return;
    }

    // Validáció futtatása
    if (!validateForm()) {
      setError('Kérjük, töltse ki az összes kötelező mezőt helyesen!');
      return;
    }

    try {
      setSaving(true);
      if (isViewingOtherUser && userId) {
        await userProfileService.updateUserProfileById(userId, formData);
      } else {
        await userProfileService.updateMyProfile(formData);
      }
      showSuccess('A profil módosítva.');
      setFieldErrors({});
      // Az alapvonal a MENTÉS SIKERÉRE lép, nem az azt követő újratöltés sikerére [N-14,
      // 2026-09-14]: a `loadProfile()` elnyeli a saját hibáját (csak `setError`-t hív), tehát egy
      // hálózati hiba után a SIKERES mentés ellenére „mentetlen adat" maradt bejelentve — a
      // bezárás rákérdezett, az F5-öt pedig a `beforeunload` blokkolta, holott minden elmentve
      // volt. Ugyanez a minta, mint az `EventEditDialog`-ban.
      setBaseline(formData);
      // A vázlatok is a MENTÉS SIKERÉRE nullázódnak, nem az azt követő újratöltésre: a `loadProfile()`
      // elnyeli a saját hibáját, tehát egy hálózati hiba után egy elavult gépelt szöveg maradt volna
      // a mezőben, miközben a mentés már megtörtént.
      setForintDrafts({});
      await loadProfile();
    } catch (err: any) {
      console.error('Error saving profile:', err);
      // [validátor M-1, 2026-10-03] lejárt munkamenet → az újra-belépő ablakkal egyező mondat
      setError(saveErrorMessage(err, 'Hiba történt a profil mentésekor'));
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('A kép mérete nem haladhatja meg az 5MB-ot');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Csak kép fájlok tölthetők fel');
      return;
    }

    try {
      setUploadingImage(true);
      setError('');
      const result = isViewingOtherUser && userId
        ? await userProfileService.uploadProfileImageForUser(userId, file)
        : await userProfileService.uploadProfileImage(file);
      // CSAK a kép-hivatkozást írjuk át, `loadProfile()` NÉLKÜL [BL-1, 2026-09-14].
      // A teljes újratöltés a szerver értékeivel FELÜLÍRTA a beírt, még nem mentett űrlapot
      // (`setFormData(loaded)`), és mert az alapvonal is vele lépett (`setBaseline(loaded)`), az
      // `isDirty` TISZTÁT jelentett: a „Bezárás" kérdés nélkül zárt, a `beforeunload` sem állt fel
      // — a felhasználó abban a hitben lépett ki, hogy a módosítása megvan. Ez pontosan az a
      // hibaosztály, ami miatt a mentetlen-változás védelem készült.
      // A szomszéd `UserProfile.tsx` ugyanezt a két műveletet már eddig is így zárta — egy
      // képernyő-pár, egy szabály.
      setProfile((prev) => (prev ? { ...prev, profileImageUrl: result.imageUrl } : prev));
      showSuccess('A profilkép feltöltve.');
    } catch (err: any) {
      console.error('Error uploading image:', err);
      setError(requestErrorMessage(err, 'Hiba történt a kép feltöltésekor'));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageDelete = async () => {
    if (!(await confirm({
      title: 'Profilkép törlése',
      message: 'Biztosan törölni szeretnéd a profilképet?',
      confirmLabel: 'Törlés',
      destructive: true,
    }))) {
      return;
    }

    try {
      setUploadingImage(true);
      setError('');
      if (isViewingOtherUser && userId) {
        await userProfileService.deleteProfileImageForUser(userId);
      } else {
        await userProfileService.deleteProfileImage();
      }
      // `loadProfile()` NÉLKÜL, a feltöltéssel azonos okból [BL-1, 2026-09-14]: az újratöltés a
      // beírt, még nem mentett űrlapot NÉMÁN visszaállította a szerver értékére, az alapvonalat
      // is léptetve — tehát a védelem „tisztát" jelentett a félbehagyott adatlapra.
      setProfile((prev) => (prev ? { ...prev, profileImageUrl: undefined } : prev));
      showSuccess('A profilkép törölve.');
    } catch (err: any) {
      console.error('Error deleting image:', err);
      setError(requestErrorMessage(err, 'Hiba történt a kép törlésekor'));
    } finally {
      setUploadingImage(false);
    }
  };

  const getProfileImageUrl = () => {
    if (!profile?.profileImageUrl) return undefined;
    const url = profile.profileImageUrl;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return `${url}?t=${new Date().getTime()}`;
    }
    return `${API_BASE_URL}${url}?t=${new Date().getTime()}`;
  };

  /**
   * Mentetlen módosítás: a betöltött állapothoz hasonlítva. A `open &&` NEM elhagyható: a
   * dialógus zárva is MOUNTOLVA marad a szülőben, a beírt (majd elvetett) értékekkel — a
   * bejelentés nélküli kapu különben zárt dialógusra is „mentetlen adatot" jelentene, és
   * blokkolná a fül-váltást.
   */
  const isDirty = open && isFormDirty(baseline, formData);
  // A BEJELENTETT NÉV = a FEJLÉC szövege [N-16, 2026-09-14]: a SAJÁT adatlapon a fejléc „Saját
  // adataim", tehát a fül-váltás kérdése sem mondhat „Felhasználó adatlapja"-t.
  useUnsavedChanges(isDirty, isViewingOtherUser ? 'Felhasználó adatlapja' : 'Saját adataim');
  /** A beágyazott `UserNotes` el nem küldött megjegyzés-vázlata (lásd `handleClose`). */
  const [notesDraftDirty, setNotesDraftDirty] = useState(false);

  /**
   * MINDEN záró út ezen megy át: a `Dialog onClose`-a (backdrop + ESC), a fejléc X-e és a
   * lábléc „Bezárás" gombja. Ha van beírt, még nem mentett adat, a KÖZÖS megerősítő ablak kérdez.
   *
   * ⚠️ KÖLCSÖNHATÁS a jelszó-visszaállítás TARTÓS Alertjével (`passwordResetMessage`): a
   * jelszó-reset NEM nyúl az űrlaphoz, tehát önmagában SOHA nem hoz létre „mentetlen módosítást" —
   * egy jelszó-reset után a bezárás kérdés nélkül megy, ahogy eddig. Ha viszont az admin közben
   * ÁT IS ÍRT egy mezőt, előbb a mentetlen-kérdés jön, és az Alertet csak akkor töröljük, ha a
   * zárás TÉNYLEG megtörtént: „Mégse" után a hitelesítő adatra vonatkozó
   * információ (mely címre ment a levél) a helyén marad, nem szívódik fel a félbehagyott zárással.
   */
  const handleClose = async () => {
    // [Web UX 2. csomag – mentetlen-védelem (8.1), 2026-10-03] a beágyazott „Új megjegyzés”
    // vázlata (`UserNotes`, `notesDraftDirty`) is a dialógus adata: korábban az adatlap tiszta
    // állapotában a bezárás kérdés nélkül eldobta. (A `UserNotes` maga is regisztrál a kapuba.)
    // [3. javító kör — validátor M, 2026-10-03] ha MINDKETTŐ piszkos, a kérdés mindkettőt megnevezi
    // (korábban csak az „adatlap" szerepelt, a megjegyzés-vázlat elvesztése kimondatlan maradt).
    // [4. javító kör, 2026-10-03] a közös „A(z) X mentetlen módosításokat tartalmaz" sablon az
    // összetett alanyra döcögős magyar mondatot adott („A(z) adatlap és az új megjegyzés …
    // tartalmaz") — a két-részes esetben saját, természetes mondat megy ki; a cím és a gombok a közös
    // sablonból jönnek (egységes „Mentetlen változás" fejléc).
    const unsavedWhat = isDirty ? 'adatlap' : 'új megjegyzés';
    const question = isDirty && notesDraftDirty
      ? {
          ...discardChangesConfirm(unsavedWhat),
          message: 'Az adatlapon és az új megjegyzésben mentetlen módosítás van. Ha most bezárod, a beírt adatok elvesznek.',
        }
      : discardChangesConfirm(unsavedWhat);
    if ((isDirty || notesDraftDirty) && !(await confirm(question))) {
      return;
    }
    setError('');
    setPasswordResetMessage(null);
    onClose();
  };

  // Determine if sensitive data should be visible
  // Coaches need SOS access, others (Admin, Player viewing own) see everything
  const canViewSensitiveData = !needsSosAccess || sosGranted;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5">
            {isViewingOtherUser ? 'Felhasználó adatlapja' : 'Saját adataim'}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {/* TARTÓS Alert: a jelszó-reset eredményét (mely címre ment a levél) az adminnak el kell
                olvasnia — a globális toast (4 mp) alatt ez könnyen elsikkad. Minden más mentés a
                `showSuccess` toastra megy, ez az egyetlen tudatos kivétel ebben a dialógusban.
                ⚠️ Jelszó NEM szerepel benne — lásd a `passwordResetMessage` deklarációját [É1]. */}
            {passwordResetMessage && (
              <Alert
                severity="success"
                sx={{ mb: 2 }}
                data-testid="password-reset-alert"
                onClose={() => setPasswordResetMessage(null)}
              >
                {passwordResetMessage}
              </Alert>
            )}

            {/* SOS Button Section (for Coaches viewing other users) */}
            {needsSosAccess && !sosGranted && (
              <Box sx={{ mb: 3 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Edző szerepkörben érzékeny adatok elrejtve. Csak a név és születési dátum látható.
                </Alert>
                {sosCheckingEligibility ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : sosEligible ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Alert severity="info">
                      {sosMessage}
                    </Alert>
                    <Button
                      variant="contained"
                      color="error"
                      size="large"
                      startIcon={sosGranting ? <CircularProgress size={20} color="inherit" /> : <EmergencyIcon />}
                      onClick={handleSosButtonClick}
                      disabled={sosGranting}
                      fullWidth
                      sx={{
                        py: 1.5,
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                      }}
                    >
                      {sosGranting ? 'Hozzáférés kérése...' : 'SOS - Érzékeny adatok megjelenítése'}
                    </Button>
                  </Box>
                ) : (
                  <Alert severity="error">
                    {sosMessage || 'Nem vagy jogosult az SOS funkcióhoz.'}
                  </Alert>
                )}
              </Box>
            )}

            {sosGranted && (
              <Alert severity="success" sx={{ mb: 2 }}>
                SOS hozzáférés aktív - Minden adat látható. Az adatok az oldal újratöltéséig maradnak láthatók.
              </Alert>
            )}

            {/* Profile Image Section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Box sx={{ position: 'relative', mb: 2 }}>
                <Avatar
                  src={getProfileImageUrl()}
                  alt={profile?.name}
                  sx={{ width: 120, height: 120 }}
                />
                {uploadingImage && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      borderRadius: '50%',
                    }}
                  >
                    <CircularProgress size={40} />
                  </Box>
                )}
              </Box>

              {canEdit && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="profile-image-upload"
                    type="file"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                  />
                  <label htmlFor="profile-image-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<PhotoCameraIcon />}
                      disabled={uploadingImage}
                      size="small"
                    >
                      Kép feltöltése
                    </Button>
                  </label>
                  {profile?.profileImageUrl && (
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={handleImageDelete}
                      disabled={uploadingImage}
                      size="small"
                    >
                      Kép törlése
                    </Button>
                  )}
                </Box>
              )}
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Profile Information */}
            <Typography variant="h6" gutterBottom>
              Személyes adatok
            </Typography>

            {profile && (
              <Box sx={{ mb: 2 }}>
                {isAdmin && isViewingOtherUser && roles.length > 0 ? (
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Szerepkör</InputLabel>
                    <Select
                      value={formData.userRoleId || ''}
                      label="Szerepkör"
                      onChange={(e) => setFormData(prev => ({ ...prev, userRoleId: e.target.value as string }))}
                      disabled={saving}
                    >
                      {roles.map((role) => (
                        <MenuItem key={role.userRoleId} value={role.userRoleId}>
                          {role.displayName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Felhasználói szerepkör: <strong>{profile.userRoleName}</strong>
                  </Typography>
                )}
              </Box>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
              <TextField
                label="Név"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                onBlur={() => handleFieldBlur('name')}
                disabled={!canEdit}
                fullWidth
                required
                error={!!fieldErrors.name}
                helperText={fieldErrors.name}
              />

              {/* [C10, 2026-10-30] A TAG E-MAILJE ÉS TELEFONJA AZ EDZŐNEK CSAK SOS-SEL. MI VOLT A BAJ: ez a
                  kapu eddig CSAK a felületen élt — a szerver SOS nélkül is kiadta mindkét adatot. Most a
                  `GET /api/user-profile/{id}` edzőnek más adatlapján SOS nélkül `null`-t ad
                  (`backend/PinyoApi/Services/UserProfileSanitizer.cs`); a két mező ezért SOS nélkül nem is
                  rajzolódik ki (nem üres mezőként), az SOS-gomb újratölti és feloldja. Admin és saját: változatlan.
                  Őr: `__tests__/UserProfileDialog.c10Contact.test.tsx`. */}
              {canViewSensitiveData && (
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={() => handleFieldBlur('email')}
                  disabled={!canEdit}
                  fullWidth
                  required
                  error={!!fieldErrors.email}
                  helperText={fieldErrors.email}
                />
              )}

              {canViewSensitiveData && (
                <TextField
                  label="Telefonszám"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  onBlur={() => handleFieldBlur('phone')}
                  disabled={!canEdit}
                  fullWidth
                  required
                  error={!!fieldErrors.phone}
                  helperText={fieldErrors.phone}
                />
              )}

              <TextField
                required
                label="Születési dátum"
                name="dateOfBirth"
                type="date"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                onBlur={() => handleFieldBlur('dateOfBirth')}
                disabled={!canEdit}
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: DOB_MIN, max: dobMax() }}
                error={!!fieldErrors.dateOfBirth}
                helperText={fieldErrors.dateOfBirth}
              />

              {canViewSensitiveData && groupTrainings.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Preferált edzések
                  </Typography>
                  {Object.entries(
                    groupTrainings.reduce<Record<string, PreferredTrainingInfo[]>>((acc, t) => {
                      const venue = t.venueName || 'Nincs helyszín';
                      (acc[venue] = acc[venue] || []).push(t);
                      return acc;
                    }, {})
                  )
                    .sort(([a], [b]) => a.localeCompare(b, 'hu'))
                    .map(([venue, trainings]) => {
                      const selectedCount = trainings.filter(t => (formData.preferredTrainingIds || []).includes(t.plannedEventId)).length;
                      return (
                        <Accordion key={venue} disableGutters sx={{ '&:before': { display: 'none' }, boxShadow: 'none', border: '1px solid', borderColor: 'divider', mb: 1 }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5, alignItems: 'center', gap: 0.5 } }}>
                            <LocationIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                              {venue}
                            </Typography>
                            {selectedCount > 0 && (
                              <Typography variant="caption" sx={{ ml: 'auto', mr: 1, color: 'primary.main', fontWeight: 'bold' }}>
                                {selectedCount} kiválasztva
                              </Typography>
                            )}
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0, px: { xs: 1, sm: 2 } }}>
                            <FormGroup>
                              {Object.entries(
                                trainings.reduce<Record<string, PreferredTrainingInfo[]>>((acc, t) => {
                                  (acc[t.dayOfWeek] = acc[t.dayOfWeek] || []).push(t);
                                  return acc;
                                }, {})
                              )
                                .sort(([a], [b]) => (DAY_ORDER[a] ?? 7) - (DAY_ORDER[b] ?? 7))
                                .map(([day, dayTrainings]) => (
                                  <Box key={day} sx={{ mb: 1 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                                      {DAY_NAMES_HU[day] || day}
                                    </Typography>
                                    {dayTrainings.map((t) => (
                                      <Box key={t.plannedEventId} sx={{ display: 'flex', alignItems: 'flex-start', ml: 1 }}>
                                        <Checkbox
                                          checked={(formData.preferredTrainingIds || []).includes(t.plannedEventId)}
                                          onChange={() => handleTrainingToggle(t.plannedEventId)}
                                          disabled={!canEdit}
                                          size="small"
                                          sx={{ p: 0.5 }}
                                        />
                                        <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' }, pt: 0.5 }}>
                                          {`${t.startTime}-${t.endTime} ${t.eventName}`}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                ))}
                            </FormGroup>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Parent Information (for Players) */}
            {isPlayerRole(profile?.userRoleSystemName, profile?.userRoleName) && canViewSensitiveData && (
              <>
                <Typography variant="h6" gutterBottom>
                  Szülői adatok
                </Typography>
                {getAge() !== null && getAge()! < 18 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    18 év alatti játékosok esetén kötelező a szülő nevének és telefonszámának megadása
                  </Alert>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                  <TextField
                    label="Szülő neve"
                    name="parentName"
                    value={formData.parentName}
                    onChange={handleInputChange}
                    onBlur={() => handleFieldBlur('parentName')}
                    disabled={!canEdit}
                    fullWidth
                    required={getAge() !== null && getAge()! < 18}
                    error={!!fieldErrors.parentName}
                    helperText={fieldErrors.parentName}
                  />

                  <TextField
                    label="Szülő telefonszáma"
                    name="parentPhone"
                    value={formData.parentPhone}
                    onChange={handleInputChange}
                    onBlur={() => handleFieldBlur('parentPhone')}
                    disabled={!canEdit}
                    fullWidth
                    required={getAge() !== null && getAge()! < 18}
                    error={!!fieldErrors.parentPhone}
                    helperText={fieldErrors.parentPhone}
                  />
                </Box>
                <Divider sx={{ my: 3 }} />
              </>
            )}

            {/*
              Magánóra-díj — CSAK admin látja és állítja (az edző SOS-sel sem). A klub kérése: a
              játékoshoz személyre szabott árat lehessen megadni, és a jelenléti íven ezt kelljen
              beszedni. A mező eddig csak a felhasználó-kezelés külön űrlapján volt, amit a
              „Szerkesztés" gomb már nem nyit meg — meglévő játékosnál sehol nem lehetett beírni.
            */}
            {isPlayerRole(profile?.userRoleSystemName, profile?.userRoleName) && isAdmin && (
              <>
                <Typography variant="h6" gutterBottom>
                  Magánóra
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                  <TextField
                    label="Magánóra-díj (Ft)"
                    name="privateLessonFee"
                    // `type="text"` + `inputMode="numeric"`, NEM `type="number"` [BL-2]: a
                    // szám-input a `2.800` alakot érvényes számként adta át (`parseFloat` → 2,8 Ft),
                    // és a nem értelmezhető tartalomra ÜRES `value`-t ad. A bevitelt a közös
                    // számjegy-szűrő tartja egész forinton (`utils/money.ts`).
                    type="text"
                    value={forintFieldText('privateLessonFee')}
                    onChange={handleInputChange}
                    disabled={!canEdit}
                    fullWidth
                    inputProps={{ inputMode: 'numeric', 'data-testid': 'profile-private-lesson-fee-input' }}
                    inputRef={(el: HTMLInputElement | null) => { forintInputRefs.current.privateLessonFee = el; }}
                    error={!!fieldErrors.privateLessonFee}
                    // A mentés-őr hibája ELŐZI a magyarázó súgót [M2]: a hiba fontosabb, és
                    // a mező alatt csak EGY sor van.
                    helperText={fieldErrors.privateLessonFee
                      || "A játékos egyéni magánóra-díja; a kedvezmény alacsonyabb szám. Üresen hagyható — akkor a jelenléti íven nincs beszedendő összeg."}
                  />
                </Box>
                <Divider sx={{ my: 3 }} />
              </>
            )}

            {/* Billing Information (for Players) */}
            {/*
              SZEREPKÖR-EGYEZTETÉS — miért magyar a döntő ág, és miért marad az angol alias
              [NB-7, 2026-09-14]:

              A szerver a `UserRole.DisplayName`-et adja, tehát MAGYARUL. MÉRVE élő API-val
              (2026-09-14): `GET /api/user-profile` → „Adminisztrátor", `/{játékos}` → „Játékos",
              `/{edző}` → „Edző". Az angol `'Player'`/`'Coach'` ezeken a végpontokon SOSEM jön.

              Az angol alias ezért TŰRÉS, nem élő út — és SZÁNDÉKOSAN marad: a backend MÁSUTT
              ugyanebbe a névbe az angol `.Name`-et írja (`UserProfileController:346,455` az
              audit-naplóba, `PlannedEventsController:201,307,807` a résztvevő-DTO-ba), tehát egy
              újabb hívó könnyen angol alakot hozna. A tűrés ára semmi, a kivételének kockázata
              viszont egy eltűnő pénz-mező.

              ✅ FRISSÍTVE [B-W4, 2026-09-16]: a döntés MÁR NEM a megjelenített névből megy. A
              szerver ADDITÍVAN adja a GÉPI nevet is (`UserProfileDto.UserRoleSystemName` =
              `UserRole.Name`), és a `utils/roles.ts` ELSŐSORBAN azt olvassa; a fenti magyar/angol
              tűrés innentől TARTALÉK, arra az esetre, ha a gépi név nem jött meg (régi
              válasz-alak, régi tesztfixture). MIÉRT KELLETT: a „Edző"/„Játékos" címke a
              szerepkör-kezelésben ÁTNEVEZHETŐ — attól a pillanattól ezek a pénz-mezők (óradíj,
              bankszámla, magánóra-díj) NÉMÁN eltűntek volna a felületről.

              AMI AZ NB-7 LELET VOLT: a komponens-tesztek fixture-jei ANGOL alakot adtak
              (`userRoleName: 'Player'`), amit a szerver soha nem ad. Emiatt NEM ŐRIZTÉK a valódi
              utat: ha valaki a MAGYAR ágat vette volna ki (mert a `UserManagement` az angol
              `UserRole.name`-mel dolgozik), a tesztek ZÖLDEK maradtak volna, miközben a
              magánóra-díj / óradíj mező eltűnik a VALÓDI felületről. A fixture-ök ezért 2026-09-14
              óta MAGYARUL állnak (`UserProfileDialog.fee/unsaved/confirm`, `UserProfile.*`).
            */}
            {isPlayerRole(profile?.userRoleSystemName, profile?.userRoleName) && canViewSensitiveData && (
              <>
                <Typography variant="h6" gutterBottom>
                  Számlázási adatok
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                  <TextField
                    label="Számlázási név"
                    name="billingName"
                    value={formData.billingName}
                    onChange={handleInputChange}
                    disabled={!canEdit}
                    fullWidth
                  />

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="Irányítószám"
                      name="billingZipCode"
                      value={formData.billingZipCode}
                      onChange={handleInputChange}
                      disabled={!canEdit}
                      sx={{ width: '30%' }}
                    />
                    <TextField
                      label="Város"
                      name="billingCity"
                      value={formData.billingCity}
                      onChange={handleInputChange}
                      disabled={!canEdit}
                      sx={{ width: '70%' }}
                    />
                  </Box>

                  <TextField
                    label="Utca, házszám"
                    name="billingStreetAddress"
                    value={formData.billingStreetAddress}
                    onChange={handleInputChange}
                    disabled={!canEdit}
                    fullWidth
                  />

                  <TextField
                    label="Adószám"
                    name="taxNumber"
                    value={formData.taxNumber}
                    onChange={handleInputChange}
                    disabled={!canEdit}
                    fullWidth
                  />
                </Box>
                <Divider sx={{ my: 3 }} />
              </>
            )}

            {/* Financial Information (for Coaches) */}
            {isCoachRole(profile?.userRoleSystemName, profile?.userRoleName) && canViewSensitiveData && (
              <>
                <Typography variant="h6" gutterBottom>
                  Pénzügyi adatok
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                  {/*
                    BANKSZÁMLASZÁM — ADMINNAK, EDZŐ SZEREPKÖRNÉL [B-W8, 2026-09-16].

                    MI VOLT A BAJ: a `bankAccount` az ablak ÁLLAPOTÁBAN ott volt (`:96`, `:370`),
                    BEVITELI MEZŐ nélkül. A szám tehát csak a felhasználó-kezelés LÉTREHOZÓ
                    űrlapján volt megadható („Bankszámlaszám (opcionális)",
                    `UserManagement.tsx:924`) — amit a „Szerkesztés" gomb már nem nyit meg. Egy
                    MEGLÉVŐ edzőnél a számlaszámot SEHOL nem lehetett beírni vagy javítani, pedig
                    kifizetéskor ide kell utalni.

                    ⚠️ AZ ÜRES MEZŐ NEM TÖRÖL — ÉS EZT A FELÜLETEN IS KI KELL MONDANI: a szerver
                    az üres/hiányzó értéket „ne nyúlj hozzá"-ként kezeli
                    (`UserProfileController`, B-S8), mert amíg NEM VOLT mező, a mentés azt küldte
                    vissza, amit betöltött — egy feltétel nélküli írás az első mentéskor
                    KITÖRÖLTE volna a meglévő számlaszámot. Ez a szemantika szándékos; a törlés
                    felületi útja NYITOTT KLUB-DÖNTÉS, ezért a súgó megmondja, mit tegyen az
                    admin, ha tényleg törölni akar.
                  */}
                  {isAdmin && (
                    <TextField
                      label="Bankszámlaszám"
                      name="bankAccount"
                      value={formData.bankAccount || ''}
                      onChange={handleInputChange}
                      disabled={!canEdit}
                      fullWidth
                      inputProps={{ 'data-testid': 'profile-bank-account-input' }}
                      helperText="Ide utaljuk a kifizetést. Üresen hagyva a korábbi számlaszám megmarad — a törlését az adminisztrátor a rendszergazdától kérheti."
                    />
                  )}

                  {/* [Web 6 W1, 2026-10-05] Csak adminnak vagy a saját adatlapon — lásd `canSeeHourlyRate`. */}
                  {canSeeHourlyRate && (
                    <TextField
                      label="Óradíj (Ft)"
                      name="hourlyRate"
                      // `type="text"` + `inputMode="numeric"` [BL-2] — lásd a Magánóra-díj mezőt.
                      // A megjelenítés `forintFieldValue` (`?? ''`), NEM `|| ''` [N-12]: a `0 || ''`
                      // ÜRES mezőt adott, miközben az állapotban 0 volt — a felhasználó üres mezőt
                      // látott, a mentetlen-változás védelem viszont piszkosnak jelentette.
                      type="text"
                      value={forintFieldText('hourlyRate')}
                      onChange={handleInputChange}
                      disabled={!canEdit}
                      fullWidth
                      inputProps={{ inputMode: 'numeric', 'data-testid': 'profile-hourly-rate-input' }}
                      inputRef={(el: HTMLInputElement | null) => { forintInputRefs.current.hourlyRate = el; }}
                      error={!!fieldErrors.hourlyRate}
                      helperText={fieldErrors.hourlyRate}
                    />
                  )}

                  <TextField
                    label="Számlázási név"
                    name="billingName"
                    value={formData.billingName}
                    onChange={handleInputChange}
                    disabled={!canEdit}
                    fullWidth
                  />

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="Irányítószám"
                      name="billingZipCode"
                      value={formData.billingZipCode}
                      onChange={handleInputChange}
                      disabled={!canEdit}
                      sx={{ width: '30%' }}
                    />
                    <TextField
                      label="Város"
                      name="billingCity"
                      value={formData.billingCity}
                      onChange={handleInputChange}
                      disabled={!canEdit}
                      sx={{ width: '70%' }}
                    />
                  </Box>

                  <TextField
                    label="Utca, házszám"
                    name="billingStreetAddress"
                    value={formData.billingStreetAddress}
                    onChange={handleInputChange}
                    disabled={!canEdit}
                    fullWidth
                  />
                </Box>
                <Divider sx={{ my: 3 }} />
              </>
            )}

            {/* Preferred Trainings Section */}
            {profile?.preferredTrainings && profile.preferredTrainings.length > 0 && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FitnessCenterIcon /> Tervezett edzések
                </Typography>
                {(() => {
                  const dayOrder: Record<string, number> = { Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6, Sunday: 7 };
                  const dayNames: Record<string, string> = { Monday: 'Hétfő', Tuesday: 'Kedd', Wednesday: 'Szerda', Thursday: 'Csütörtök', Friday: 'Péntek', Saturday: 'Szombat', Sunday: 'Vasárnap' };
                  const grouped: Record<string, PreferredTrainingInfo[]> = {};
                  for (const pt of profile.preferredTrainings) {
                    const venue = pt.venueName || 'Nincs helyszín';
                    if (!grouped[venue]) grouped[venue] = [];
                    grouped[venue].push(pt);
                  }
                  for (const venue of Object.keys(grouped)) {
                    grouped[venue].sort((a, b) => (dayOrder[a.dayOfWeek] || 8) - (dayOrder[b.dayOfWeek] || 8) || a.startTime.localeCompare(b.startTime));
                  }
                  return Object.entries(grouped).map(([venue, events]) => (
                    <Box key={venue} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <LocationIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="subtitle2" color="text.secondary">{venue}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 3 }}>
                        {events.map((pe) => (
                          <Box key={pe.plannedEventId} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={pe.eventTypeName || pe.eventName}
                              size="small"
                              sx={{ bgcolor: pe.eventTypeColor || '#1976d2', color: '#fff', fontWeight: 500, minWidth: 80 }}
                            />
                            <Typography variant="body2">
                              {dayNames[pe.dayOfWeek] || pe.dayOfWeek} {pe.startTime}–{pe.endTime}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ));
                })()}
              </>
            )}

            {/* User Notes Section */}
            {profile && (
              <>
                <Typography variant="h6" gutterBottom>
                  Felhasználói jegyzetek
                </Typography>
                <UserNotes userId={profile.userId} userRoleName={profile.userRoleName} onDirtyChange={setNotesDraftDirty} />
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {isAdmin && isViewingOtherUser && (
          <Button
            startIcon={isResettingPassword ? <CircularProgress size={20} /> : <LockResetIcon />}
            onClick={handleResetPasswordClick}
            color="warning"
            disabled={isResettingPassword}
            sx={{ mr: 'auto' }}
          >
            Jelszó alaphelyzetbe
          </Button>
        )}
        <Button onClick={handleClose}>
          Bezárás
        </Button>
        {canEdit && (
          <Button
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            variant="contained"
          >
            Mentés
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UserProfileDialog;
